import { useMemo, memo } from 'react';
import { DATA_TYPE_PROP_MAP } from "../common/AgGrid/index.jsx";

import { project_entry_path } from '~/routes';
import MessageCountHeader from '../common/LiveDiscussion/MessageCountHeader.jsx';

const MonitorInfoCellRenderer = memo((params) => {
  const { project_id: { id, label } = {} } = params.data;
  return (
    <a href={project_entry_path({project_id: params.data.id})}>{label}</a>
  );
});

const COLUMN_DEFS = [
  {
    field: '-',
    colId: 'global_search',
    editable: false,
    hide: true,
    lockVisible: true,
    filter: 'agTextColumnFilter',
    suppressColumnsToolPanel: true
  }
];

const valueGetter = (params) => {
  params.data ||= {};
  return (params.data[params.colDef.colId]||{})['label'] ?? params.data[params.colDef.colId] ?? '';
};

const selectionValueFormatter = ({value, colDef: { filterParams }}) => {
  if (Array.isArray(value)) {
    return value.map(val => filterParams.optionMap.get(val) || 'undefined').join(', ')
  }

  return filterParams.optionMap.get(value);;
}

const ADDITIONAL_INFO_CATEGORY = 'ProgrammeFormProjectAdditionalInfo';

export const useMonitoringColumnDefs = ({sorted_columns, default_visible_columns, visible_columns, column_state = []}) => {
  const visible_cols = useMemo(() => {
    const visible = (column_state || []).length
      ? column_state.map(c => !c.hide ? c.colId : null).filter(Boolean)
      : default_visible_columns;
    return [...visible, 'global_search'];
  }, [column_state, default_visible_columns]);

  const columnDefs = useMemo(() => {
    const sorted_visible_columns = sorted_columns.map((c) => {
      const dataType = DATA_TYPE_PROP_MAP[c['data_type']] || c['data_type'];
      const filter = DATA_TYPE_PROP_MAP[dataType]?.filter || 'agTextColumnFilter';
      return {
        field: c['label'],
        colId: c['value'],
        editable: false,
        sortable: true,
        filter: filter,
        hide: !visible_cols.includes(String(c['value'])),
        toolPanelClass: c.category === ADDITIONAL_INFO_CATEGORY ? 'additional-info' : '',
        valueGetter: valueGetter,
        ...(c['value'] === 'project_id' && {
          filter: 'agTextColumnFilter',
          cellRenderer: MonitorInfoCellRenderer,
          sort: 'asc',
        }),
        ...(c['value'] === 'title' && {
          flex: 1,
          minWidth: 100,
          cellRenderer: ({data}) => {
            return <a href={project_entry_path({project_id: data.id})}>{data.title}</a>;
          }
        }),
        ...(c['value'] === 'payments' && {
          cellRenderer: (params) => {
            let str = '';
            const data = valueGetter(params);
          
            if (data?.total) {
              Object.keys(data.total).forEach((currency, index) => {
                if (index !== 0) str += ', ';
                str += `${data.paid[currency]} / ${data.total[currency]}`;
                if (currency !== 'none') str += ` ${currency.toUpperCase()}`;
              });
            }

            return <span dangerouslySetInnerHTML={{ __html: str }} />;
          }
        }),
        ...(Array.isArray(c['selection_options']) && {
          filter: 'agSetColumnFilter',
          filterParams: {
            values: c['selection_options'].map((op) => op.value),
            optionMap: new Map(c['selection_options'].map(c => [c.value, c.label])),
            valueFormatter: selectionValueFormatter
          },
        }),
        ...(c['value'] === 'open_discussion' && {
          pinned: 'left',
          filter: false,
          sortable: false,
          headerComponent: () => <MessageCountHeader title={I18n.t('open_discussion_tooltip')} />
        })
      }
    });

    return COLUMN_DEFS.concat(sorted_visible_columns.filter((c) => c !== null));
  }, [visible_cols, sorted_columns]);

  return {
    columnDefs
  };
};
